'use client'

import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import type { ReviewCardData } from '../content/reviewTypes'
import { StarRating } from './StarRating'
import { ReviewerAvatar } from './ReviewerAvatar'
import { SourceBadge } from './SourceBadge'
import { reviewCountLabel } from './reviewsFormat'

export type ReviewTeaserItem =
  | { kind: 'aggregate'; companyName: string; average: number; count: number }
  | { kind: 'review'; review: ReviewCardData }

export type ReviewTeaserCardProps = {
  item: ReviewTeaserItem
  /** false = fade out. The host's phase machine owns which. */
  visible: boolean
  /** Skip the transitions (prefers-reduced-motion). */
  reducedMotion?: boolean
  /**
   * Card body clicked — open the hub. Omit when the hub cannot actually open
   * (hub disabled, or an empty pool with no panel to show) — the body then
   * renders as a plain non-interactive element instead of a button, so the
   * card never announces an affordance it can't honor.
   */
  onOpen?: () => void
  /** ✕ clicked — end the whole sequence. */
  onDismiss: () => void
}

/**
 * One card in the staged reviews teaser sequence. Renders either the
 * aggregate (stars + "<Company> is rated N from X reviews") or one real
 * review — content is `cached_reviews` and the computed aggregate only, no
 * authored copy beyond the fixed connective phrasing (no-fabrication).
 *
 * Non-blocking and unpositioned: `role="status" aria-live="polite"` so a
 * screen reader announces it once without stealing focus, and the host places
 * it in the floating column. The host owns timing, frequency and sequencing.
 *
 * The body is a `<button>` only when `onOpen` is provided. The teaser and the
 * hub panel are gated by independent per-dealer settings, so a config where
 * the teaser plays but no panel exists to open is real and supported — in
 * that case the body must not claim to be interactive.
 */
export function ReviewTeaserCard({
  item,
  visible,
  reducedMotion = false,
  onOpen,
  onDismiss,
}: ReviewTeaserCardProps) {
  const [entered, setEntered] = useState(reducedMotion)

  useEffect(() => {
    if (reducedMotion) {
      // Enter-animation trigger: with reduced motion, settle immediately (no
      // rAF). A one-shot mount setState, not a cascading render.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEntered(true)
      return
    }
    const id = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(id)
  }, [reducedMotion])

  const settled = entered && visible
  const motionCls = settled ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
  const transitionCls = reducedMotion ? '' : 'transition-all duration-300 ease-out'

  const body =
    item.kind === 'aggregate' ? (
      <AggregateBody companyName={item.companyName} average={item.average} count={item.count} />
    ) : (
      <ReviewBody review={item.review} />
    )

  return (
    <div
      role="status"
      aria-live="polite"
      className={`w-[min(20rem,calc(100vw-2rem))] ${transitionCls} ${motionCls}`}
    >
      <div className="relative rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            className="block w-full rounded-[var(--radius-lg)] p-4 text-left"
          >
            {body}
          </button>
        ) : (
          <div className="block w-full rounded-[var(--radius-lg)] p-4 text-left">{body}</div>
        )}

        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute right-2 top-2 rounded-md p-1 text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-secondary)]"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

function AggregateBody({
  companyName,
  average,
  count,
}: {
  companyName: string
  average: number
  count: number
}) {
  return (
    <>
      <StarRating rating={average} className="text-xl" />
      <span
        className="mt-2 block pr-5 text-sm leading-relaxed text-[var(--color-text-secondary)]"
        style={{ fontFamily: 'var(--font-body)' }}
      >
        <strong
          className="font-semibold text-[var(--color-text-primary)]"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {companyName}
        </strong>{' '}
        is rated {average.toFixed(1)} from {reviewCountLabel(count)}.
      </span>
    </>
  )
}

function ReviewBody({ review }: { review: ReviewCardData }) {
  return (
    <>
      <span className="flex items-start gap-3 pr-5">
        <ReviewerAvatar src={review.reviewer_avatar} name={review.reviewer_name} sizeClass="size-10 text-base" />
        <span className="min-w-0 flex-1">
          <span
            className="block truncate text-sm font-semibold text-[var(--color-text-primary)]"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {review.reviewer_name}
          </span>
          <StarRating rating={review.rating} className="text-sm" />
        </span>
      </span>

      <span
        className="mt-2 line-clamp-3 text-sm leading-relaxed text-[var(--color-text-secondary)]"
        style={{ fontFamily: 'var(--font-body)' }}
      >
        {review.review_text}
      </span>

      {review.source_platform && (
        <span className="mt-2 block">
          <SourceBadge source={String(review.source_platform)} variant="pill" />
        </span>
      )}
    </>
  )
}
